// Singles Chain — useSinglesSession hook.
//
// Read-only consumer of get_sc_session_today() RPC.
// Returns today's session + slots, with refetch capability.

import { isSinglesUnderSelection } from "../../supabase/functions/_shared/singles-market-policy.ts";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";
import { isKickoffOnSessionDate } from "../predictions/sessionDate.ts";

// ─── Types ────────────────────────────────────────────────

export interface ScSlot {
  id: string;
  slot_number: number;
  target_odds: number;
  fixture_id: string | null;
  home_team: string | null;
  away_team: string | null;
  league_name: string | null;
  league_short: string | null;
  country_code: string | null;
  market: string | null;
  selection: string | null;
  odds: number | null;
  sport: string;
  model_probability: number | null;
  edge_pct: number | null;
  confidence: number | null;
  kickoff_at: string | null;
  reveal_at: string | null;
  outcome: "won" | "lost" | "void" | "pending" | null;
  agent_reasoning: string | null;
  public_case: {
    why: string;
    home_recent: string[];
    away_recent: string[];
    evidence_through: string;
    source: string;
    caveat: string;
    quote_source?: string;
    quoted_at?: string;
  } | null;
  simulated: boolean;
}

export interface ScSession {
  id: string;
  session_date: string;
  session_type: "2game" | "3game" | "dynamic_1game" | "dynamic_2game" | "dynamic_3game";
  primary_sport: string | null;
  sports_active: string[];
  status: "rest_day" | "scanning" | "slots_ready" | "live" | "completed" | "no_fixtures";
  simulated: boolean;
  policy_blocked?: boolean;
}

export interface UseSinglesSessionState {
  session: ScSession | null;
  slots: readonly ScSlot[];
  sessionDate: string;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// ─── Parsers ──────────────────────────────────────────────

function parseSlot(raw: Record<string, unknown>): ScSlot {
  const publicCase = raw.public_case && typeof raw.public_case === "object" && !Array.isArray(raw.public_case)
    ? raw.public_case as Record<string, unknown>
    : null;
  return {
    id: String(raw.id ?? ""),
    slot_number: Number(raw.slot_number ?? 0),
    target_odds: Number(raw.target_odds ?? 0),
    fixture_id: typeof raw.fixture_id === "string" ? raw.fixture_id : null,
    home_team: typeof raw.home_team === "string" ? raw.home_team : null,
    away_team: typeof raw.away_team === "string" ? raw.away_team : null,
    league_name: typeof raw.league_name === "string" ? raw.league_name : null,
    league_short: typeof raw.league_short === "string" ? raw.league_short : null,
    country_code: typeof raw.country_code === "string" ? raw.country_code : null,
    market: typeof raw.market === "string" ? raw.market : null,
    selection: typeof raw.selection === "string" ? raw.selection : null,
    odds: typeof raw.odds === "number" ? raw.odds : null,
    sport: String(raw.sport ?? "football"),
    model_probability: typeof raw.model_probability === "number" ? raw.model_probability : null,
    edge_pct: typeof raw.edge_pct === "number" ? raw.edge_pct : null,
    confidence: typeof raw.confidence === "number" ? raw.confidence : null,
    kickoff_at: typeof raw.kickoff_at === "string" ? raw.kickoff_at : null,
    reveal_at: typeof raw.reveal_at === "string" ? raw.reveal_at : null,
    outcome: typeof raw.outcome === "string"
      ? (raw.outcome as ScSlot["outcome"])
      : null,
    agent_reasoning: typeof raw.agent_reasoning === "string" ? raw.agent_reasoning : null,
    public_case: publicCase && typeof publicCase.why === "string" ? {
      why: publicCase.why,
      home_recent: Array.isArray(publicCase.home_recent) ? publicCase.home_recent.filter((v): v is string => typeof v === "string") : [],
      away_recent: Array.isArray(publicCase.away_recent) ? publicCase.away_recent.filter((v): v is string => typeof v === "string") : [],
      evidence_through: String(publicCase.evidence_through ?? ""),
      source: String(publicCase.source ?? ""),
      caveat: String(publicCase.caveat ?? ""),
      quote_source: typeof publicCase.quote_source === "string" ? publicCase.quote_source : undefined,
      quoted_at: typeof publicCase.quoted_at === "string" ? publicCase.quoted_at : undefined,
    } : null,
    simulated: raw.simulated !== false,
  };
}

function parseSession(raw: Record<string, unknown>): ScSession {
  return {
    id: String(raw.id ?? ""),
    session_date: String(raw.session_date ?? ""),
    session_type: (raw.session_type as ScSession["session_type"]) ?? "3game",
    primary_sport: typeof raw.primary_sport === "string" ? raw.primary_sport : null,
    sports_active: Array.isArray(raw.sports_active) ? raw.sports_active.map(String) : ["football"],
    status: (raw.status as ScSession["status"]) ?? "scanning",
    simulated: raw.simulated !== false,
    policy_blocked: raw.policy_blocked === true,
  };
}

// ─── Hook ─────────────────────────────────────────────────

export function useSinglesSession(): UseSinglesSessionState {
  const { user } = useAuthSession();
  const [session, setSession] = useState<ScSession | null>(null);
  const [slots, setSlots] = useState<readonly ScSlot[]>([]);
  const [sessionDate, setSessionDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  // Track whether we've loaded once — skip setLoading(true) on subsequent
  // fetches so the page doesn't flash/reload on tab return.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!user) {
      setSession(null);
      setSlots([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const client = getBrowserClient();
    // Only show loading spinner on first load — not on refetch/tab return.
    if (!hasLoadedOnce) setLoading(true);
    setError(null);

    void (async () => {
      try {
        const { data, error: rpcError } = await (
          client.rpc as unknown as (fn: string) => Promise<{
            data: Record<string, unknown> | null;
            error: { message: string } | null;
          }>
        )("get_sc_session_today");

        if (cancelled) return;

        if (rpcError) {
          setError(rpcError.message);
          setLoading(false);
          return;
        }

        if (!data) {
          setSession(null);
          setSlots([]);
          setSessionDate("");
          setLoading(false);
          return;
        }

        const raw = data as Record<string, unknown>;
        setSessionDate(String(raw.session_date ?? ""));

        if (raw.session && typeof raw.session === "object") {
          const parsed = parseSession(raw.session as Record<string, unknown>);
          const blockedByResponse = Array.isArray(raw.slots) && raw.slots.some((slot) =>
            slot && isSinglesUnderSelection(slot.market, slot.selection)
          );
          setSession({ ...parsed, policy_blocked: parsed.policy_blocked || blockedByResponse });
        } else {
          setSession(null);
        }

        if (Array.isArray(raw.slots)) {
          const responseSessionDate = String(raw.session_date ?? "");
          setSlots(
            (raw.slots as Record<string, unknown>[])
              .filter((s): s is Record<string, unknown> => s !== null)
              .map(parseSlot)
              .filter((slot) =>
                !isSinglesUnderSelection(slot.market, slot.selection) &&
                isKickoffOnSessionDate(slot.kickoff_at, responseSessionDate) &&
                slot.model_probability !== null && slot.model_probability >= 0.70
              ),
          );
        } else {
          setSlots([]);
        }

        setLoading(false);
        setHasLoadedOnce(true);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
        setHasLoadedOnce(true);
      }
    })();

    return () => { cancelled = true; };
  }, [user, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  return useMemo(
    () => ({ session, slots, sessionDate, loading, error, refetch }),
    [session, slots, sessionDate, loading, error, refetch],
  );
}
